import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { NormalizedRelease } from "@/types/sofa";
import { format } from "date-fns";

interface ReleaseTimelineChartProps {
  releases: NormalizedRelease[];
}

export function ReleaseTimelineChart({ releases }: ReleaseTimelineChartProps) {
  // Take latest 12 releases with CVEs, reversed to chronological order
  const data = releases
    .slice(0, 12)
    .reverse()
    .map(r => ({
      name: r.productVersion,
      label: `${r.productName} ${r.productVersion}`,
      date: format(new Date(r.releaseDate), "MMM d"),
      cves: r.cveCount,
      exploited: r.activelyExploitedCount,
      priority: r.priority,
    }));

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No CVE data available
      </div>
    );
  }

  const getBarColor = (priority: string) => {
    switch (priority) {
      case "critical": return "hsl(0, 84%, 60%)";
      case "high": return "hsl(30, 80%, 55%)";
      case "medium": return "hsl(45, 90%, 55%)";
      default: return "hsl(220, 70%, 50%)";
    }
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 10 }}
          axisLine={{ stroke: "hsl(var(--border))" }}
        />
        <YAxis
          tick={{ fontSize: 10 }}
          axisLine={{ stroke: "hsl(var(--border))" }}
          allowDecimals={false}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "hsl(var(--popover))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
            fontSize: 12,
          }}
          labelFormatter={(_, payload) => payload?.[0]?.payload?.label || ""}
          formatter={(value: number, name: string) => [
            value,
            name === "cves" ? "CVEs" : "Exploited",
          ]}
        />
        <Bar dataKey="cves" name="cves" radius={[4, 4, 0, 0]}>
          {data.map((entry, index) => (
            <Cell key={index} fill={getBarColor(entry.priority)} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
